import { DialogContentClassKey } from '@material-ui/core';
import { ClassNameMap } from '@material-ui/styles';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { Button } from '@roc/ui';
import React, { ReactNode, useEffect, useState } from 'react';
import { StandardDialog } from './dialog';
import { FlagOutlined } from '@material-ui/icons';

export interface ConfirmDialogProps {
  dialogContentclasses?: Partial<ClassNameMap<DialogContentClassKey>>;
  className?: string;
  open: boolean;
  title?: string;
  body: string | ReactNode;
  dialogLogo?: ReactNode;
  confirmButtonText?: string;
  cancelButtonText?: string;
  removeContentBox?: boolean;
  handleClose: React.ReactEventHandler<{}>;
  handleCancel: React.MouseEventHandler<HTMLButtonElement>;
  handleConfirm: React.MouseEventHandler<HTMLButtonElement>;
  fullScreen?: boolean;
}

export interface InformationDialogProps {
  dialogContentclasses?: Partial<ClassNameMap<DialogContentClassKey>>;
  className?: string;
  open: boolean;
  title?: string;
  body: string | ReactNode;
  closeButtonText?: string;
  handleClose: React.ReactEventHandler<{}>;
  maxWidth?: any;
}

const useStyles = makeStyles(() =>
  createStyles({
    dialogPaper: {
      overflow: 'visible',
      margin: 'auto',
    },
    dialogContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  }),
);


export const ConfirmDialog = ({
  dialogContentclasses,
  className,
  open,
  title = 'Confirmation',
  body,
  confirmButtonText = 'Confirm',
  cancelButtonText = 'Cancel',
  removeContentBox,
  handleClose,
  handleCancel,
  handleConfirm,
  dialogLogo,
  fullScreen
}: ConfirmDialogProps) => {
  const classes = useStyles();

  return (
    <StandardDialog
      classes={{
        paper: classes.dialogPaper,
        container: classes.dialogContainer,
      }}
      fullScreen={fullScreen}
      dialogContentclasses={dialogContentclasses}
      className={className}
      open={open}
      title={title}
      maxWidth={'sm'}
      handleClose={handleClose}
      dialogContent={body}
      dialogLogo={dialogLogo}
      removeContentBox={removeContentBox}
      dialogActions={
        <>
          <Button
            style={{ marginRight: '8px' }}
            onClick={handleCancel}
            color="primary"
            variant="outlined"
            testId="cancel"
          >
            {cancelButtonText}
          </Button>
          <Button
            onClick={handleConfirm}
            color="primary"
            variant="contained"
            testId="confirm"
            id="confirm"
          >
            {confirmButtonText}
          </Button>
        </>
      }
    />
  );
};

export interface WithConfirmDialogProps {
  onClick: (event: React.MouseEventHandler<HTMLElement>) => void;
  confirmDialogProps: {
    dialogContentclasses?: Partial<ClassNameMap<DialogContentClassKey>>;
    className?: string;
    title?: string;
    body: string | ReactNode;
    dialogLogo?: ReactNode;
    confirmButtonText?: string;
    cancelButtonText?: string;
    removeContentBox?: boolean;
  };
}

export interface WithInformationDialogProps {
  onClick: (event: React.MouseEventHandler<HTMLElement>) => void;
  informationDialogProps: {
    dialogContentclasses?: Partial<ClassNameMap<DialogContentClassKey>>;
    className?: string;
    title?: string;
    body: string | ReactNode;
    closeButtonText?: string;
    maxWidth?: any;
    dialogOpen?: boolean;
    setDialogOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  };
}

export const withConfirmDialog = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P & WithConfirmDialogProps> => ({
  onClick,
  confirmDialogProps,
  ...props
}: WithConfirmDialogProps) => {
    const {
      title,
      body,
      confirmButtonText,
      cancelButtonText,
      dialogContentclasses,
      removeContentBox,
      dialogLogo
    } = confirmDialogProps;
    const [open, setOpen] = useState(false);
    const [targetButtonEvent, setTargetButtonEvent] = useState(null);

    const onTargetButtonClick = (
      event: React.MouseEventHandler<HTMLButtonElement>
    ) => {
      setTargetButtonEvent(event);
      setOpen(true);
    };

    return (
      <>
        <ConfirmDialog
          dialogContentclasses={dialogContentclasses}
          open={open}
          title={title}
          body={body}
          confirmButtonText={confirmButtonText}
          cancelButtonText={cancelButtonText}
          removeContentBox={removeContentBox}
          handleCancel={() => setOpen(false)}
          handleClose={() => setOpen(false)}
          handleConfirm={() => {
            setOpen(false);
            onClick(targetButtonEvent);
          }}
          dialogLogo={dialogLogo}
        />
        <Component onClick={onTargetButtonClick} {...(props as P)} />
      </>
    );
  };

export const InformationDialog = ({
  dialogContentclasses,
  className,
  open,
  title = 'Information',
  body,
  closeButtonText = 'Close',
  handleClose,
  maxWidth = 'sm',
}: InformationDialogProps) => {
  return (
    <StandardDialog
      dialogContentclasses={dialogContentclasses}
      className={className}
      open={open}
      fullWidth
      title={title}
      handleClose={handleClose}
      dialogContent={body}
      dialogActions={
        <>
          <Button
            style={{ marginRight: '8px' }}
            onClick={handleClose}
            color="primary"
            variant="outlined"
            testId="cancel"
          >
            {closeButtonText}
          </Button>
        </>
      }
      maxWidth={maxWidth}
    />
  );
};

export const withInformationDialog = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P & WithInformationDialogProps> => ({
  onClick,
  informationDialogProps,
  ...props
}: WithInformationDialogProps) => {
    const {
      title,
      body,
      closeButtonText,
      dialogContentclasses,
      maxWidth,
      dialogOpen = false,
      setDialogOpen,
    } = informationDialogProps;
    const [internalOpen, setInternalOpen] = useState(dialogOpen);
    const [targetButtonEvent, setTargetButtonEvent] = useState(null);
    const isControlled = typeof setDialogOpen === 'function';

    useEffect(() => {
      if (!isControlled) {
        setInternalOpen(dialogOpen);
      }
    }, [dialogOpen, isControlled]);

    const getOpenState = () => (isControlled ? dialogOpen : internalOpen);

    const setOpenState = (value: boolean) => {
      if (isControlled && setDialogOpen) {
        setDialogOpen(value);
      } else {
        setInternalOpen(value);
      }
    };


    const onTargetButtonClick = (
      event: React.MouseEventHandler<HTMLButtonElement>
    ) => {
      setTargetButtonEvent(event);
      setOpenState(true);
    };

    return (
      <>
        <InformationDialog
          dialogContentclasses={dialogContentclasses}
          open={getOpenState()}
          title={title}
          body={body}
          closeButtonText={closeButtonText}
          handleClose={() => setOpenState(false)}
          maxWidth={maxWidth}
        />
        <Component onClick={onTargetButtonClick} {...(props as P)} />
      </>
    );
  };
